import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTheme } from 'styled-components';

import { CheckboxSize } from '../../theme/Form';
import { Icon, Text } from '../../ui';
import { datasetToAttributes } from '../../utils';
import {
  StyledCheckbox,
  StyledCheckmark,
  StyledInput,
  StyledLabel,
  StyledWrapper,
} from './Checkbox.style';

export interface CheckboxProps {
  id?: string;
  label?: string;
  size?: keyof typeof CheckboxSize;
  checked?: boolean;
  disabled?: boolean;
  onClick: (checked: boolean, event?: React.MouseEvent | React.TouchEvent) => void;
  onBeforeChange?: (
    checked: boolean,
    event?: React.MouseEvent | React.TouchEvent
  ) => boolean | void;
  dataset?: Record<string, string>;
}

export function Checkbox({
  id,
  label,
  size = 'Medium',
  checked = false,
  disabled = false,
  onClick,
  onBeforeChange,
  dataset,
}: CheckboxProps) {
  const theme = useTheme();
  const [isChecked, setIsChecked] = useState(checked);

  const handleOnClick = useCallback(
    (event: React.MouseEvent | React.TouchEvent) => {
      if (disabled) return;

      event.stopPropagation();

      setIsChecked((state) => {
        const newValue = !state;

        if (onBeforeChange && onBeforeChange(newValue, event) === false) {
          return state;
        }

        onClick(newValue, event);

        return newValue;
      });
    },
    [disabled, onBeforeChange, onClick]
  );

  useEffect(() => {
    setIsChecked(checked);
  }, [checked]);

  const dataProps = useMemo(() => datasetToAttributes(dataset), [dataset]);

  return (
    <StyledWrapper id={id} onClick={handleOnClick} disabled={disabled} {...dataProps}>
      <StyledCheckbox disabled={disabled} checkboxSize={size}>
        <StyledInput
          type="checkbox"
          checkboxSize={size}
          checked={isChecked}
          disabled={disabled}
          readOnly
        />
        <StyledCheckmark>
          {isChecked && (
            <Icon
              name={'check-selected'}
              color={disabled ? theme.Color.Neutrals.Light : theme.Color.System.Primary}
              size={size}
            />
          )}
        </StyledCheckmark>
      </StyledCheckbox>

      {label && (
        <StyledLabel>
          <Text
            size="Body"
            color={disabled ? theme.Color.Neutrals.Gray : theme.Color.Neutrals.Dark}
          >
            {label}
          </Text>
        </StyledLabel>
      )}
    </StyledWrapper>
  );
}
